import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PackageCheck } from "lucide-react";
import axios from "axios";
import { inr } from "@/pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const DISPATCH_STEPS = [
  ["not_dispatched", "Order received"],
  ["packed", "Packed"],
  ["dispatched", "Dispatched"],
  ["in_transit", "In transit"],
  ["out_for_delivery", "Out for delivery"],
  ["delivered", "Delivered"],
];
const fmtDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "long", year: "numeric" });

function OrderStatus({ o }) {
  const step = DISPATCH_STEPS.findIndex(([k]) => k === o.dispatch_status);
  const cancelled = o.fulfillment_status === "cancelled";
  return (
    <div className="track-result" data-testid="track-result">
      <div className="track-head">
        <div>
          <p className="eyebrow red">ORDER {o.order_id}</p>
          <h2 data-testid="track-status">{cancelled ? "Cancelled" : DISPATCH_STEPS[Math.max(step, 0)][1]}</h2>
        </div>
        <div className="track-eta">
          <span>Expected delivery</span>
          <b data-testid="track-expected-delivery">{o.expected_delivery ? fmtDate(o.expected_delivery) : "To be confirmed"}</b>
        </div>
      </div>
      {!cancelled && (
        <ol className="track-steps" data-testid="track-steps">
          {DISPATCH_STEPS.map(([k, label], i) => (
            <li key={k} className={i <= step ? "done" : ""}>
              <span />
              {label}
            </li>
          ))}
        </ol>
      )}
      {o.tracking_notes && (
        <div className="track-notes" data-testid="track-notes">
          <span>Update from our team</span>
          <p>{o.tracking_notes}</p>
        </div>
      )}
      <div className="track-meta">
        <span>Placed {new Date(o.created_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}</span>
        <span>Payment: {o.payment_status === "paid" ? "Paid" : o.payment_status === "failed" ? "Failed" : "Awaiting payment"}</span>
        <span>Total {inr(o.total_inr)}</span>
        {o.tracking_updated_at && <span>Last updated {new Date(o.tracking_updated_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>}
      </div>
      <ul className="track-lines">
        {o.lines.map((l, i) => (
          <li key={i}>
            {l.quantity}× {l.brand} {l.name} <small>{l.variant}</small>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function TrackOrderPage() {
  const [form, setForm] = useState({ order_id: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setOrder(null);
    try {
      const r = await axios.post(`${API}/orders/track`, { order_id: form.order_id.trim(), email: form.email.trim() });
      setOrder(r.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Please enter a valid order number and email address.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="request-page track-page">
      <div className="request-intro">
        <p className="eyebrow red">ORDERS / TRACKING</p>
        <h1>
          Track
          <br />
          <i>my order.</i>
        </h1>
        <p>Enter the order number from your confirmation (e.g. TTC-1A2B3C4D) and the email you used at checkout.</p>
        <p className="support-links">
          <PackageCheck size={15} /> Need help? <Link to="/support" data-testid="track-support-link">Contact support</Link>
        </p>
      </div>
      <div>
        <form className="request-form" onSubmit={submit} data-testid="track-form">
          <label>
            Order number *
            <input required minLength={4} maxLength={40} value={form.order_id} onChange={(e) => setForm({ ...form, order_id: e.target.value })} placeholder="TTC-XXXXXXXX" data-testid="track-order-id" />
          </label>
          <label>
            Email *
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="track-email" />
          </label>
          {error && <p className="form-error wide" data-testid="track-error">{error}</p>}
          <button className="btn btn-red" disabled={busy} data-testid="track-submit">
            {busy ? "Checking…" : "Track order"} <ArrowRight size={16} />
          </button>
        </form>
        {order && <OrderStatus o={order} />}
      </div>
    </main>
  );
}
